import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupIcon from '@mui/icons-material/Group';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import AddIcon from '@mui/icons-material/Add';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import trainingApi from '../../services/trainingApi';

const STATUS_COLORS = {
  active: 'success',
  deferred: 'warning',
  withdrawn: 'error',
  completed: 'info',
};

// ---------------------------------------------------------------------------
// Overview Tab
// ---------------------------------------------------------------------------
const OverviewTab = ({ cohort, stats }) => {
  const [copied, setCopied] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createType, setCreateType] = useState('general_link');
  const [createForm, setCreateForm] = useState({ maxUses: '', expiresAt: '', description: '', welcomeMessage: '', email: '', name: '', showLogo: false });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editInvitation, setEditInvitation] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [qrLabel, setQrLabel] = useState('');

  const cohortId = cohort?._id;

  const loadInvitations = useCallback(async () => {
    if (!cohortId) return;
    try {
      setInvLoading(true);
      const res = await trainingApi.listCohortInvitations(cohortId);
      const body = res.data || res;
      const data = body.data || body;
      setInvitations(data.invitations || data || []);
    } catch { /* ignore */ }
    finally { setInvLoading(false); }
  }, [cohortId]);

  useEffect(() => { loadInvitations(); }, [loadInvitations]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setCreateError(null);
      if (createType === 'general_link') {
        await trainingApi.createCohortGeneralLink(cohortId, {
          maxUses: createForm.maxUses ? parseInt(createForm.maxUses) : undefined,
          expiresAt: createForm.expiresAt || undefined,
          description: createForm.description || undefined,
          welcomeMessage: createForm.welcomeMessage || undefined,
          showLogo: createForm.showLogo || false,
        });
      } else {
        await trainingApi.createCohortIndividualInvite(cohortId, {
          email: createForm.email,
          name: createForm.name || undefined,
        });
      }
      setCreateDialogOpen(false);
      setCreateForm({ maxUses: '', expiresAt: '', description: '', welcomeMessage: '', email: '', name: '', showLogo: false });
      loadInvitations();
    } catch (err) {
      setCreateError(err.response?.data?.error || err.message || 'Failed to create invitation');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (invId) => {
    if (!window.confirm('Revoke this invitation? It will no longer be usable.')) return;
    try {
      await trainingApi.revokeCohortInvitation(cohortId, invId);
      loadInvitations();
    } catch { /* ignore */ }
  };

  const openEdit = (inv) => {
    setEditInvitation(inv);
    setEditForm({
      maxUses: inv.maxUses ?? '',
      expiresAt: inv.expiresAt ? new Date(inv.expiresAt).toISOString().split('T')[0] : '',
      description: inv.generalLinkSettings?.description || '',
      welcomeMessage: inv.generalLinkSettings?.welcomeMessage || '',
      tag: inv.generalLinkSettings?.tag || '',
      showServiceGroupLogo: inv.displaySettings?.showServiceGroupLogo || false,
      status: inv.status || 'active',
    });
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleEdit = async () => {
    if (!editInvitation) return;
    try {
      setEditing(true);
      setEditError(null);
      const updates = { ...editForm };
      if (updates.maxUses === '') updates.maxUses = null;
      if (updates.expiresAt) updates.expiresAt = new Date(updates.expiresAt).toISOString();
      await trainingApi.updateCohortInvitation(cohortId, editInvitation._id, updates);
      setEditDialogOpen(false);
      loadInvitations();
    } catch (err) {
      setEditError(err.response?.data?.error || err.message || 'Failed to update invitation');
    } finally {
      setEditing(false);
    }
  };

  const getInvUrl = (inv) => {
    const base = window.location.origin;
    if (inv.signupCode) return `${base}/login?code=${inv.signupCode}`;
    if (inv.invitationToken) return `${base}/login?invitation=${inv.invitationToken}`;
    return '';
  };

  const showQr = (inv) => {
    const url = getInvUrl(inv);
    setQrUrl(url);
    setQrLabel(inv.signupCode || inv.invitedEmail || 'Invitation');
    setQrDialogOpen(true);
  };

  const statItems = [
    { label: 'Total Students', value: stats?.totalStudents ?? '-' },
    { label: 'Lecturers', value: stats?.totalLecturers ?? '-' },
    { label: 'Assignments', value: stats?.totalAssignments ?? '-' },
    { label: 'Sessions', value: stats?.totalSessions ?? '-' },
    { label: 'Average Score', value: stats?.averageScore != null ? `${Math.round(stats.averageScore)}%` : '-' },
    { label: 'Pass Rate', value: stats?.passRate != null ? `${Math.round(stats.passRate)}%` : '-' },
  ];

  const activeInvitations = invitations.filter(i => i.status === 'active');
  const inactiveInvitations = invitations.filter(i => i.status !== 'active');

  return (
    <Box>
      {/* Stats Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 2, mb: 4 }}>
        {statItems.map((item) => (
          <Paper key={item.label} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>{item.value}</Typography>
            <Typography variant="body2" color="text.secondary">{item.label}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Cohort Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Cohort Settings</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Box><Typography variant="body2" color="text.secondary">Name</Typography><Typography>{cohort?.name || '-'}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Status</Typography><Chip label={cohort?.status || 'active'} color={STATUS_COLORS[cohort?.status] || 'default'} size="small" /></Box>
          <Box><Typography variant="body2" color="text.secondary">Academic Year</Typography><Typography>{cohort?.academicYear || '-'}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Start Date</Typography><Typography>{cohort?.startDate ? new Date(cohort.startDate).toLocaleDateString() : '-'}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">End Date</Typography><Typography>{cohort?.endDate ? new Date(cohort.endDate).toLocaleDateString() : '-'}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Code</Typography><Typography sx={{ fontFamily: 'monospace' }}>{cohort?.code || '-'}</Typography></Box>
        </Box>
      </Paper>

      {/* Invitations */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Invitations</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" onClick={() => { setCreateType('individual'); setCreateDialogOpen(true); setCreateError(null); }}>
              Individual Invite
            </Button>
            <Button variant="contained" size="small" onClick={() => { setCreateType('general_link'); setCreateDialogOpen(true); setCreateError(null); }}>
              Create General Link
            </Button>
          </Box>
        </Box>

        {invLoading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : invitations.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No invitations yet. Create a general link or individual invitation to allow students to join this cohort.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Code / Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Uses</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...activeInvitations, ...inactiveInvitations].map(inv => (
                <TableRow key={inv._id} hover>
                  <TableCell>
                    <Chip size="small" label={inv.signupCode ? 'General Link' : 'Individual'} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: inv.signupCode ? 'monospace' : 'inherit', fontWeight: 600 }}>
                      {inv.signupCode || inv.invitedEmail || '-'}
                    </Typography>
                    {inv.invitedName && <Typography variant="caption" color="text.secondary">{inv.invitedName}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={inv.status}
                      color={inv.status === 'active' ? 'success' : inv.status === 'revoked' ? 'error' : inv.status === 'depleted' ? 'warning' : 'default'}
                      variant="outlined" />
                  </TableCell>
                  <TableCell>{inv.currentUses || 0}{inv.maxUses ? ` / ${inv.maxUses}` : ' / unlimited'}</TableCell>
                  <TableCell>{inv.expiresAt ? new Date(inv.expiresAt).toLocaleDateString() : 'Never'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                      <IconButton size="small" onClick={() => handleCopy(getInvUrl(inv))}><ContentCopyIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {inv.signupCode && (
                      <Tooltip title="Show QR Code">
                        <IconButton size="small" onClick={() => showQr(inv)}><QrCodeIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {inv.signupCode && inv.status === 'active' && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(inv)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {inv.status === 'active' && (
                      <Tooltip title="Revoke">
                        <IconButton size="small" color="error" onClick={() => handleRevoke(inv._id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Create Invitation Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{createType === 'general_link' ? 'Create General Link' : 'Create Individual Invitation'}</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {createType === 'general_link' ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Create a signup link that multiple students can use to join this cohort. Share via email, LMS, or display as QR code.
                </Typography>
                <TextField label="Max Uses" type="number" value={createForm.maxUses}
                  onChange={e => setCreateForm(f => ({ ...f, maxUses: e.target.value }))}
                  helperText="Leave empty for unlimited uses" inputProps={{ min: 1 }} />
                <TextField label="Expiry Date" type="date" value={createForm.expiresAt}
                  onChange={e => setCreateForm(f => ({ ...f, expiresAt: e.target.value }))}
                  InputLabelProps={{ shrink: true }} helperText="Default: 90 days from now" />
                <TextField label="Description" value={createForm.description}
                  onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="e.g. Year 4 Psychiatry 2026 intake" />
                <TextField label="Welcome Message" multiline rows={2} value={createForm.welcomeMessage}
                  onChange={e => setCreateForm(f => ({ ...f, welcomeMessage: e.target.value }))}
                  placeholder="Optional message shown to students on signup" />
                <FormControlLabel
                  control={<Switch checked={createForm.showLogo} onChange={e => setCreateForm(f => ({ ...f, showLogo: e.target.checked }))} />}
                  label="Show organisation logo on signup page"
                />
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Send a personal invitation to a specific student by email.
                </Typography>
                <TextField label="Student Email" required type="email" value={createForm.email}
                  onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} />
                <TextField label="Student Name (optional)" value={createForm.name}
                  onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || (createType === 'individual' && !createForm.email.trim())}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Invitation Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Invitation</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Tag / Label" value={editForm.tag || ''}
              onChange={e => setEditForm(f => ({ ...f, tag: e.target.value }))} />
            <TextField label="Description" value={editForm.description || ''}
              onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
            <TextField label="Welcome Message" multiline rows={2} value={editForm.welcomeMessage || ''}
              onChange={e => setEditForm(f => ({ ...f, welcomeMessage: e.target.value }))} />
            <TextField label="Max Uses" type="number" value={editForm.maxUses ?? ''}
              onChange={e => setEditForm(f => ({ ...f, maxUses: e.target.value }))}
              helperText="Leave empty for unlimited" inputProps={{ min: 1 }} />
            <TextField label="Expiry Date" type="date" value={editForm.expiresAt || ''}
              onChange={e => setEditForm(f => ({ ...f, expiresAt: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
            <TextField label="Status" select value={editForm.status || 'active'}
              onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <FormControlLabel
              control={<Switch checked={editForm.showServiceGroupLogo || false}
                onChange={e => setEditForm(f => ({ ...f, showServiceGroupLogo: e.target.checked }))} />}
              label="Show organisation logo on signup page"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editing}>
            {editing ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>QR Code — {qrLabel}</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          {qrUrl && (
            <Box sx={{ display: 'inline-block', p: 2, bgcolor: 'white', borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <img
                id="qr-code-img"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(qrUrl)}`}
                alt="QR Code"
                style={{ width: 256, height: 256 }}
              />
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, wordBreak: 'break-all' }}>{qrUrl}</Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button size="small" variant="outlined" onClick={() => handleCopy(qrUrl)}>
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Students Tab
// ---------------------------------------------------------------------------
const StudentsTab = ({ cohort, onEnrolStudents, onWithdrawStudent }) => {
  const [enrolDialogOpen, setEnrolDialogOpen] = useState(false);
  const [enrolMode, setEnrolMode] = useState('select'); // 'select' or 'email'
  const [emailsText, setEmailsText] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [enrolLoading, setEnrolLoading] = useState(false);
  const [enrolError, setEnrolError] = useState(null);
  const [enrolSuccess, setEnrolSuccess] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);

  const students = cohort?.students || [];
  const enrolledUserIds = students.map(s => (s.userId?._id || s.userId || '').toString());

  const openEnrolDialog = async () => {
    setEnrolDialogOpen(true);
    setEnrolMode('select');
    setEnrolError(null);
    setEnrolSuccess(null);
    setSelectedUserIds([]);
    setEmailsText('');
    try {
      setLoadingMembers(true);
      const orgId = cohort?.organisationId?._id || cohort?.organisationId;
      if (!orgId) return;
      const res = await trainingApi.getOrgMembers(orgId);
      const body = res.data || res;
      const data = body.data || body;
      const members = data.members || data || [];
      // Filter to students not already enrolled
      const eligible = members.filter(m => {
        const roles = m.orgRoles || m.roles || [];
        const isStudent = roles.some(r => ['student'].includes(r));
        const alreadyEnrolled = enrolledUserIds.includes((m._id || '').toString());
        return isStudent && !alreadyEnrolled && m.isActive !== false;
      });
      setAvailableMembers(eligible);
    } catch { setAvailableMembers([]); }
    finally { setLoadingMembers(false); }
  };

  const handleEnrol = async () => {
    if (enrolMode === 'select') {
      if (selectedUserIds.length === 0) {
        setEnrolError('Please select at least one student.');
        return;
      }
      setEnrolLoading(true);
      setEnrolError(null);
      setEnrolSuccess(null);
      try {
        await trainingApi.enrolStudents(cohort._id, { userIds: selectedUserIds });
        setEnrolSuccess(`Successfully enrolled ${selectedUserIds.length} student(s).`);
        setSelectedUserIds([]);
        setTimeout(() => { setEnrolDialogOpen(false); setEnrolSuccess(null); }, 1500);
        // Trigger parent reload
        await onEnrolStudents([]);
      } catch (err) {
        setEnrolError(err?.response?.data?.error || err?.message || 'Failed to enrol students.');
      } finally {
        setEnrolLoading(false);
      }
    } else {
      const emails = emailsText.split('\n').map(e => e.trim()).filter(e => e.length > 0);
      if (emails.length === 0) {
        setEnrolError('Please enter at least one email address.');
        return;
      }
      setEnrolLoading(true);
      setEnrolError(null);
      setEnrolSuccess(null);
      try {
        await onEnrolStudents(emails);
        setEnrolSuccess(`Successfully enrolled ${emails.length} student(s).`);
        setEmailsText('');
        setTimeout(() => { setEnrolDialogOpen(false); setEnrolSuccess(null); }, 1500);
      } catch (err) {
        setEnrolError(err?.response?.data?.error || err?.message || 'Failed to enrol students.');
      } finally {
        setEnrolLoading(false);
      }
    }
  };

  const toggleUser = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleWithdraw = async (userId) => {
    setWithdrawing(userId);
    try {
      await onWithdrawStudent(userId);
    } catch (err) {
      console.error('[CohortDetail] Withdraw error:', err);
    } finally {
      setWithdrawing(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Students ({students.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={openEnrolDialog}
        >
          Enrol Students
        </Button>
      </Box>

      {students.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <GroupIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            No students enrolled yet. Click "Enrol Students" to add students from your organisation or by email.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Enrolled Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((student) => {
                const user = student.userId || student.user || student;
                const userId = user._id || user.id || student.userId;
                const name = user.name || user.fullName || '-';
                const email = user.email || '-';
                const status = student.status || 'active';
                const enrolledAt = student.enrolledAt || student.createdAt;

                return (
                  <TableRow key={userId} hover>
                    <TableCell>{name}</TableCell>
                    <TableCell>{email}</TableCell>
                    <TableCell>
                      <Chip
                        label={status}
                        color={STATUS_COLORS[status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {enrolledAt ? new Date(enrolledAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Withdraw student">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleWithdraw(userId)}
                          disabled={withdrawing === userId}
                        >
                          {withdrawing === userId ? (
                            <CircularProgress size={18} />
                          ) : (
                            <PersonRemoveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Enrol Dialog */}
      <Dialog
        open={enrolDialogOpen}
        onClose={() => !enrolLoading && setEnrolDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enrol Students</DialogTitle>
        <DialogContent>
          {enrolError && <Alert severity="error" sx={{ mb: 2 }}>{enrolError}</Alert>}
          {enrolSuccess && <Alert severity="success" sx={{ mb: 2 }}>{enrolSuccess}</Alert>}

          {/* Mode toggle */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button size="small" variant={enrolMode === 'select' ? 'contained' : 'outlined'} onClick={() => setEnrolMode('select')}>
              Select from Organisation
            </Button>
            <Button size="small" variant={enrolMode === 'email' ? 'contained' : 'outlined'} onClick={() => setEnrolMode('email')}>
              Enter Emails
            </Button>
          </Box>

          {enrolMode === 'select' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select students from your organisation to enrol in this cohort. Students must first be added to the organisation via the Members tab.
              </Typography>
              {loadingMembers ? (
                <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
              ) : availableMembers.length === 0 ? (
                <Alert severity="info">
                  No eligible students found. Add students to your training organisation first via the Training Admin dashboard Members tab, or use the "Enter Emails" option.
                </Alert>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {availableMembers.map(m => (
                    <Box key={m._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderBottom: 1, borderColor: 'divider', cursor: 'pointer', bgcolor: selectedUserIds.includes(m._id) ? 'primary.50' : 'transparent', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => toggleUser(m._id)}>
                      <Switch size="small" checked={selectedUserIds.includes(m._id)} onChange={() => toggleUser(m._id)} />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{m.name || m.email}</Typography>
                        {m.name && <Typography variant="caption" color="text.secondary">{m.email}</Typography>}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              {selectedUserIds.length > 0 && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  {selectedUserIds.length} student{selectedUserIds.length !== 1 ? 's' : ''} selected
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter student email addresses, one per line. Students must already have an account in the system.
              </Typography>
              <TextField
                multiline rows={6} fullWidth
                placeholder={"student1@example.com\nstudent2@example.com\nstudent3@example.com"}
                value={emailsText} onChange={(e) => setEmailsText(e.target.value)}
                disabled={enrolLoading} variant="outlined"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrolDialogOpen(false)} disabled={enrolLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEnrol}
            disabled={enrolLoading || (enrolMode === 'select' ? selectedUserIds.length === 0 : !emailsText.trim())}
          >
            {enrolLoading ? <CircularProgress size={20} /> : 'Enrol'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Lecturers Tab
// ---------------------------------------------------------------------------
const LecturersTab = ({ cohort, onAssignLecturer, onRemoveLecturer }) => {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [lecturerRole, setLecturerRole] = useState('assistant');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [assignSuccess, setAssignSuccess] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [availableLecturers, setAvailableLecturers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const lecturers = cohort?.lecturers || [];
  const assignedUserIds = lecturers.map(l => (l.userId?._id || l.userId || '').toString());

  const openAssignDialog = async () => {
    setAssignDialogOpen(true);
    setSelectedUserId('');
    setLecturerRole('assistant');
    setAssignError(null);
    setAssignSuccess(null);
    try {
      setLoadingMembers(true);
      const orgId = cohort?.organisationId?._id || cohort?.organisationId;
      if (!orgId) return;
      const res = await trainingApi.getOrgMembers(orgId);
      const body = res.data || res;
      const data = body.data || body;
      const members = data.members || data || [];
      // Filter to lecturers/training_admins not already assigned
      const eligible = members.filter(m => {
        const roles = m.orgRoles || m.roles || [];
        const hasLecturerRole = roles.some(r => ['lecturer', 'training_admin'].includes(r));
        const alreadyAssigned = assignedUserIds.includes((m._id || '').toString());
        return hasLecturerRole && !alreadyAssigned && m.isActive !== false;
      });
      setAvailableLecturers(eligible);
    } catch {
      setAvailableLecturers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUserId) {
      setAssignError('Please select a lecturer.');
      return;
    }

    setAssignLoading(true);
    setAssignError(null);
    setAssignSuccess(null);
    try {
      await onAssignLecturer({ userId: selectedUserId, role: lecturerRole });
      setAssignSuccess('Lecturer assigned successfully.');
      setSelectedUserId('');
      setLecturerRole('assistant');
      setTimeout(() => {
        setAssignDialogOpen(false);
        setAssignSuccess(null);
      }, 1500);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to assign lecturer.';
      setAssignError(msg);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRemove = async (userId) => {
    setRemoving(userId);
    try {
      await onRemoveLecturer(userId);
    } catch (err) {
      console.error('[CohortDetail] Remove lecturer error:', err);
    } finally {
      setRemoving(null);
    }
  };

  const getRoleChip = (role) => {
    if (role === 'lead') {
      return <Chip label="Lead" color="primary" size="small" />;
    }
    return <Chip label="Assistant" variant="outlined" size="small" />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Lecturers ({lecturers.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={openAssignDialog}
        >
          Assign Lecturer
        </Button>
      </Box>

      {lecturers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <SchoolIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            No lecturers assigned. Click "Assign Lecturer" to add one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Assigned Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lecturers.map((lecturer) => {
                const user = lecturer.userId || lecturer.user || lecturer;
                const userId = user._id || user.id || lecturer.userId;
                const name = user.name || user.fullName || '-';
                const email = user.email || '-';
                const role = lecturer.role || 'assistant';
                const assignedAt = lecturer.assignedAt || lecturer.createdAt;

                return (
                  <TableRow key={userId} hover>
                    <TableCell>{name}</TableCell>
                    <TableCell>{email}</TableCell>
                    <TableCell>{getRoleChip(role)}</TableCell>
                    <TableCell>
                      {assignedAt ? new Date(assignedAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove lecturer">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemove(userId)}
                          disabled={removing === userId}
                        >
                          {removing === userId ? (
                            <CircularProgress size={18} />
                          ) : (
                            <PersonRemoveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Assign Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => !assignLoading && setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Lecturer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a lecturer from your organisation's members. Lecturers must first be added to the organisation via the Training Admin dashboard.
          </Typography>
          {assignError && <Alert severity="error" sx={{ mb: 2 }}>{assignError}</Alert>}
          {assignSuccess && <Alert severity="success" sx={{ mb: 2 }}>{assignSuccess}</Alert>}
          {loadingMembers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
          ) : availableLecturers.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No eligible lecturers found. Add lecturers to your training organisation first via the Members tab in Training Admin.
            </Alert>
          ) : (
            <TextField
              select fullWidth label="Select Lecturer"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={assignLoading}
              sx={{ mb: 2 }}
            >
              {availableLecturers.map(m => (
                <MenuItem key={m._id} value={m._id}>
                  {m.name || m.email} {m.name ? `(${m.email})` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            fullWidth
            label="Role"
            value={lecturerRole}
            onChange={(e) => setLecturerRole(e.target.value)}
            disabled={assignLoading}
          >
            <MenuItem value="lead">Lead</MenuItem>
            <MenuItem value="assistant">Assistant</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assignLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={assignLoading || !selectedUserId}
          >
            {assignLoading ? <CircularProgress size={20} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Progress Tab
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Scenarios Tab
// ---------------------------------------------------------------------------
const CohortScenariosTab = ({ cohort }) => {
  const [scenarioAccess, setScenarioAccess] = useState([]);
  const [allScenarios, setAllScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const cohortId = cohort?._id;
  const orgId = cohort?.organisationId?._id || cohort?.organisationId;

  const loadData = useCallback(async () => {
    if (!cohortId) return;
    try {
      setLoading(true);
      setError(null);
      const [accessRes, scenariosRes] = await Promise.all([
        trainingApi.getCohortScenarios(cohortId),
        trainingApi.listScenarios({ limit: 200 }),
      ]);
      const aBody = accessRes.data || accessRes;
      const aData = aBody.data || aBody;
      setScenarioAccess(aData.scenarioAccess || []);

      const sBody = scenariosRes.data || scenariosRes;
      const sData = sBody.data || sBody;
      setAllScenarios(sData.scenarios || sData || []);
    } catch {
      setError('Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAccessChange = async (scenarioId, access) => {
    try {
      setError(null);
      await trainingApi.setCohortScenarioAccess(cohortId, scenarioId, access);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleRemove = async (scenarioId) => {
    try {
      setError(null);
      await trainingApi.removeCohortScenario(cohortId, scenarioId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove');
    }
  };

  const handleAdd = async (scenarioId) => {
    try {
      setError(null);
      await trainingApi.setCohortScenarioAccess(cohortId, scenarioId, 'library');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add');
    }
  };

  const assignedIds = scenarioAccess.map(e => (e.scenarioId?._id || e.scenarioId || '').toString());
  const availableScenarios = allScenarios.filter(s =>
    !assignedIds.includes(s._id) && s.status === 'published'
  );

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Cohort Scenarios ({scenarioAccess.length})</Typography>
          <Typography variant="body2" color="text.secondary">
            Control which scenarios students in this cohort can access. Overrides the organisation-level access setting.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
          Add Scenario
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {scenarioAccess.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No cohort-level scenario overrides. Students will see scenarios based on the organisation-level access settings.
          Add scenarios here to customise what this cohort can access.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Scenario</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Org Access</TableCell>
                <TableCell>Cohort Access</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scenarioAccess.map(entry => {
                const scenario = entry.scenarioId || {};
                const sId = scenario._id || entry.scenarioId;
                return (
                  <TableRow key={sId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{scenario.name || 'Unknown'}</Typography>
                      <Typography variant="caption" color="text.secondary">{scenario.slug || ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {(scenario.category || '').replace(/_/g, ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={scenario.difficulty || '—'} variant="outlined" /></TableCell>
                    <TableCell>
                      <Chip size="small" label={scenario.studentAccess || 'hidden'}
                        color={scenario.studentAccess === 'library' ? 'success' : scenario.studentAccess === 'assignment_only' ? 'warning' : 'default'}
                        variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select size="small" variant="standard"
                        value={entry.access}
                        onChange={(e) => handleAccessChange(sId, e.target.value)}
                        sx={{ minWidth: 140 }}
                      >
                        <MenuItem value="library">Library</MenuItem>
                        <MenuItem value="assignment_only">Assignment Only</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove from cohort">
                        <IconButton size="small" color="error" onClick={() => handleRemove(sId)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Scenario Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Scenario to Cohort</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a scenario to make available to students in this cohort. You can set it to "Library" (browsable) or "Assignment Only".
          </Typography>
          {availableScenarios.length === 0 ? (
            <Alert severity="info">All available scenarios have already been added to this cohort.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Difficulty</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableScenarios.map(s => (
                  <TableRow key={s._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{s.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {(s.category || '').replace(/_/g, ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={s.difficulty || '—'} variant="outlined" /></TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => { handleAdd(s._id); }}>
                        Add to Cohort
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ProgressTab = ({ cohortId }) => {
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  const loadProgress = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await trainingApi.getCohortProgress(cohortId);
      const body = res.data || res;
      const data = body.data || body;
      setProgress(data.students || data.progress || data || []);
    } catch (err) {
      console.error('[ProgressTab] Load error:', err);
      setError('Failed to load student progress.');
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrder('asc');
    }
  };

  const sortedProgress = React.useMemo(() => {
    const arr = [...progress];
    arr.sort((a, b) => {
      let aVal, bVal;
      switch (orderBy) {
        case 'name':
          aVal = (a.name || a.studentName || '').toLowerCase();
          bVal = (b.name || b.studentName || '').toLowerCase();
          break;
        case 'sessionsCompleted':
          aVal = a.sessionsCompleted ?? a.completedSessions ?? 0;
          bVal = b.sessionsCompleted ?? b.completedSessions ?? 0;
          break;
        case 'averageScore':
          aVal = a.averageScore ?? a.avgScore ?? 0;
          bVal = b.averageScore ?? b.avgScore ?? 0;
          break;
        case 'lastActive':
          aVal = a.lastActive ? new Date(a.lastActive).getTime() : 0;
          bVal = b.lastActive ? new Date(b.lastActive).getTime() : 0;
          break;
        default:
          aVal = 0;
          bVal = 0;
      }
      if (typeof aVal === 'string') {
        return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return order === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return arr;
  }, [progress, orderBy, order]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button size="small" onClick={loadProgress} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (progress.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <TrendingUpIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">
          No progress data available yet. Students need to complete sessions first.
        </Typography>
      </Paper>
    );
  }

  const columns = [
    { id: 'name', label: 'Student Name' },
    { id: 'sessionsCompleted', label: 'Sessions Completed' },
    { id: 'averageScore', label: 'Average Score' },
    { id: 'lastActive', label: 'Last Active' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Student Progress</Typography>
        <Tooltip title="Refresh progress data">
          <IconButton onClick={loadProgress}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedProgress.map((student, idx) => {
              const name = student.name || student.studentName || '-';
              const sessions = student.sessionsCompleted ?? student.completedSessions ?? 0;
              const avgScore = student.averageScore ?? student.avgScore;
              const lastActive = student.lastActive;

              return (
                <TableRow key={student.studentId || student._id || idx} hover>
                  <TableCell>{name}</TableCell>
                  <TableCell>{sessions}</TableCell>
                  <TableCell>
                    {avgScore != null ? (
                      <Chip
                        label={`${Math.round(avgScore)}%`}
                        size="small"
                        color={avgScore >= 70 ? 'success' : avgScore >= 50 ? 'warning' : 'error'}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {lastActive ? new Date(lastActive).toLocaleDateString() : '-'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const CohortDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [cohort, setCohort] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [invitation, setInvitation] = useState(null);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  const loadCohort = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await trainingApi.getCohort(id);
      const body = res.data || res;
      const data = body.data || body;
      setCohort(data.cohort || data);

      // Extract existing invitation if present
      const cohortData = data.cohort || data;
      if (cohortData.invitation) {
        setInvitation(cohortData.invitation);
      }
    } catch (err) {
      console.error('[CohortDetail] Load error:', err);
      setError('Failed to load cohort details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadStats = useCallback(async () => {
    try {
      const res = await trainingApi.getCohortStats(id);
      const body = res.data || res;
      const data = body.data || body;
      setStats(data.stats || data);
    } catch (err) {
      console.error('[CohortDetail] Stats load error:', err);
    }
  }, [id]);

  useEffect(() => {
    loadCohort();
    loadStats();
  }, [loadCohort, loadStats]);

  const handleGenerateInvitation = async () => {
    setInvitationLoading(true);
    setActionError(null);
    try {
      const res = await trainingApi.generateInvitation(id);
      const body = res.data || res;
      const data = body.data || body;
      setInvitation(data.invitation || data);
    } catch (err) {
      console.error('[CohortDetail] Generate invitation error:', err);
      setActionError('Failed to generate invitation code.');
    } finally {
      setInvitationLoading(false);
    }
  };

  const handleEnrolStudents = async (emails) => {
    await trainingApi.enrolStudents(id, { emails });
    await loadCohort();
    await loadStats();
  };

  const handleWithdrawStudent = async (userId) => {
    setActionError(null);
    try {
      await trainingApi.removeStudent(id, userId);
      await loadCohort();
      await loadStats();
    } catch (err) {
      console.error('[CohortDetail] Withdraw error:', err);
      setActionError('Failed to withdraw student.');
    }
  };

  const handleAssignLecturer = async (data) => {
    await trainingApi.assignLecturer(id, data);
    await loadCohort();
    await loadStats();
  };

  const handleRemoveLecturer = async (userId) => {
    setActionError(null);
    try {
      await trainingApi.removeLecturer(id, userId);
      await loadCohort();
      await loadStats();
    } catch (err) {
      console.error('[CohortDetail] Remove lecturer error:', err);
      setActionError('Failed to remove lecturer.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {cohort?.name || 'Cohort Details'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {cohort?.status && (
              <Chip
                label={cohort.status}
                color={STATUS_COLORS[cohort.status] || 'default'}
                size="small"
                sx={{ mr: 1 }}
              />
            )}
            Created {cohort?.createdAt ? new Date(cohort.createdAt).toLocaleDateString() : '-'}
          </Typography>
        </Box>
        <Tooltip title="Refresh data">
          <IconButton onClick={() => { loadCohort(); loadStats(); }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, val) => setActiveTab(val)}
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab icon={<AssignmentIcon />} iconPosition="start" label="Overview" />
        <Tab icon={<GroupIcon />} iconPosition="start" label="Students" />
        <Tab icon={<SchoolIcon />} iconPosition="start" label="Lecturers" />
        <Tab icon={<LibraryBooksIcon />} iconPosition="start" label="Scenarios" />
        <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Progress" />
      </Tabs>

      {/* Tab Content */}
      {activeTab === 0 && (
        <OverviewTab
          cohort={cohort}
          stats={stats}
        />
      )}
      {activeTab === 1 && (
        <StudentsTab
          cohort={cohort}
          onEnrolStudents={handleEnrolStudents}
          onWithdrawStudent={handleWithdrawStudent}
        />
      )}
      {activeTab === 2 && (
        <LecturersTab
          cohort={cohort}
          onAssignLecturer={handleAssignLecturer}
          onRemoveLecturer={handleRemoveLecturer}
        />
      )}
      {activeTab === 3 && (
        <CohortScenariosTab cohort={cohort} />
      )}
      {activeTab === 4 && (
        <ProgressTab cohortId={id} />
      )}
    </Box>
  );
};

export default CohortDetailPage;
